import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";

import {
  CalendarFold,
  Users,
  LogOut,
  Inbox,
  NotebookTabs,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  X,
  Save,
} from "lucide-react";

// Auth0 Configuration
const config = {
  domain: "vms-optimus.us.auth0.com",
  clientId: "ZxowKlLpWMVvNbUuzMKooWx0FA5jhxYg",
  audience: "https://vms-optimus.us.auth0.com/api/v2/",
};

const getAccessToken = async () => {
  const response = await fetch(`https://${config.domain}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_id: config.clientId,
      client_secret:
        "XTH0sFAFWTqpZe8gYn9PJtVU4rg-6hctN7PROhojbsQJ2ZSVy7Nabk5kZSoihlnm",
      audience: config.audience,
      grant_type: "client_credentials",
      scope:
        "read:users read:users_app_metadata update:users_app_metadata read:user_idp_tokens read:client_grants create:users update:users delete:users read:clients read:client_credentials create:client_credentials read:roles update:roles create:roles",
    }),
  });
  const data = await response.json();
  return data.access_token;
};

// Custom Confirmation Modal Component
const ConfirmationModal = ({ show, message, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <p className="text-gray-800 text-base mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-[#ce5841] text-white rounded-lg hover:bg-orange-700 font-medium transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal Component
const ContactModal = ({
  showModal,
  editingContact,
  formData,
  setFormData,
  handleCloseModal,
  handleSaveContact,
  selectedRecipients,
  setSelectedRecipients,
  allUsers,
}) => {
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  if (!showModal) return null;

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isAdmin = currentUser?.role === "Admin";

  // Filter users based on search input
  const filteredUsers = allUsers.filter((user) => {
    const searchLower = recipientSearch.toLowerCase();
    const isNotSelected = !selectedRecipients.includes(user.email);
    const matchesSearch =
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower);
    return isNotSelected && matchesSearch;
  });

  const handleAddRecipient = (email) => {
    if (!selectedRecipients.includes(email)) {
      setSelectedRecipients([...selectedRecipients, email]);
    }
    setRecipientSearch("");
    setShowSuggestions(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingContact ? "Edit Contact" : "Add New Contact"}
          </h2>
          <button
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600"
              placeholder="Enter full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) =>
                setFormData({ ...formData, company: e.target.value })
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Enter company name"
            />
          </div>

          {isAdmin && (
            <>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.shared}
                    onChange={(e) =>
                      setFormData({ ...formData, shared: e.target.checked })
                    }
                    className="w-4 h-4 text-[#ce5841] bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Shared Contact
                  </span>
                </label>
              </div>

              {formData.shared && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Share with Recipients
                  </label>

                  {/* Searchable input with suggestions */}
                  <div className="relative">
                    <input
                      type="text"
                      value={recipientSearch}
                      onChange={(e) => {
                        setRecipientSearch(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Type to search users..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />

                    {/* Suggestions dropdown */}
                    {showSuggestions && recipientSearch && filteredUsers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredUsers.map((user) => (
                          <button
                            key={user.email}
                            type="button"
                            onClick={() => handleAddRecipient(user.email)}
                            className="w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 text-sm">
                              {user.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.email}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results message */}
                    {showSuggestions && recipientSearch && filteredUsers.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <p className="text-sm text-gray-500 text-center">
                          No users found
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Display selected recipients as chips */}
                  {selectedRecipients.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedRecipients.map((email) => {
                        const user = allUsers.find((u) => u.email === email);
                        return (
                          <div
                            key={email}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm"
                          >
                            <span>{user?.name || email}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRecipients(
                                  selectedRecipients.filter((e) => e !== email)
                                );
                              }}
                              className="hover:bg-orange-200 rounded-full p-0.5"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    {selectedRecipients.length === 0
                      ? "Type to search and add users"
                      : `${selectedRecipients.length} recipient${
                          selectedRecipients.length > 1 ? "s" : ""
                        } selected`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCloseModal}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveContact}
            className="px-4 py-2 bg-[#ce5841] text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {editingContact ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddressBook = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(7);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [hoveredContact, setHoveredContact] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    shared: false,
  });
  const [allUsers, setAllUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const isAdmin = currentUser?.role === "Admin";

  const API_BASE_URL = "https://njs-01.optimuslab.space/api/visits/contacts";

  // Fetch all users from Auth0
  const fetchAllUsers = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${config.audience}users?per_page=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const users = await response.json();
        setAllUsers(
          users.map((user) => {
            // Try multiple possible locations for username
            const username =
              user.user_metadata?.username ||
              user.name ||
              user.nickname ||
              (user.given_name && user.family_name
                ? `${user.given_name} ${user.family_name}`
                : null) ||
              user.email.split("@")[0];

            return {
              email: user.email,
              name: username,
            };
          })
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Fetch contacts from API (used for manual refresh)
  const fetchContacts = async () => {
    const userEmail = currentUser?.email;

    // Don't fetch if we don't have a user email yet
    if (!userEmail) {
      console.log("Waiting for user email...");
      return;
    }

    setLoading(true);
    try {
      const url = `${API_BASE_URL}?userEmail=${encodeURIComponent(userEmail)}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to fetch contacts:", errorData);
        alert(`Failed to load contacts: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      alert("Error loading contacts");
    } finally {
      setLoading(false);
    }
  };

  // Fetch contacts on mount and when user email changes
  useEffect(() => {
    if (currentUser?.email) {
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${config.audience}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const storedUser = localStorage.getItem("user");
          const parsedUser = storedUser ? JSON.parse(storedUser) : null;

          const auth0User = data.find((u) => u.email === parsedUser?.email);

          if (auth0User) {
            // Debug: Log the full Auth0 user object to see what fields are available
            console.log("Auth0 User Object:", auth0User);

            // Try multiple possible locations for username
            const username =
              auth0User.user_metadata?.username || // Custom user metadata
              auth0User.name || // Full name field
              auth0User.nickname || // Nickname field
              (auth0User.given_name && auth0User.family_name
                ? `${auth0User.given_name} ${auth0User.family_name}`
                : null) || // First + Last name
              auth0User.email.split("@")[0]; // Fallback to email prefix

            const userData = {
              username: username,
              email: auth0User.email,
              sub: auth0User.sub,
              role: parsedUser?.role || "Employee",
            };

            console.log("Storing user data:", userData);
            localStorage.setItem("user", JSON.stringify(userData));
            setCurrentUser(userData); // Update state to trigger re-render
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAllUsers();
    }
  }, [isAdmin]);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading user data...</div>
      </div>
    );
  }

  const ShareDetailsTooltip = ({ contact, allUsers }) => {
    if (!contact.shared || !contact.isOwnContact) return null;

    return (
      <div className="absolute left-0 -translate-x-1/2 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-[100]">
        {/* Recipients */}
        {contact.recipients && contact.recipients.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Shared with {contact.recipients.length} recipient
              {contact.recipients.length > 1 ? "s" : ""}:
            </p>
            <div className="flex flex-wrap gap-1">
              {contact.recipients.map((recipientEmail) => {
                const user = allUsers.find((u) => u.email === recipientEmail);
                return (
                  <span
                    key={recipientEmail}
                    className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                  >
                    {user?.name || recipientEmail}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Acceptance Stats e */}
        {contact.shareSummary && (
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Shared Status:
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded font-medium">
                {contact.shareSummary.accepted || 0} accepted
              </span>
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded font-medium">
                {contact.shareSummary.pending || 0} pending
              </span>
              <span className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded font-medium">
                {contact.shareSummary.rejected || 0} rejected
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleNewContact = async () => {
    setEditingContact(null);
    setFormData({
      name: "",
      email: "",
      company: "",
      shared: false, // Always default to false for new contacts
    });
    setSelectedRecipients([]);

    if (isAdmin) {
      await fetchAllUsers();
    }

    setShowModal(true);
  };

  const handleEditContact = async (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      company: contact.company,
      shared: contact.shared,
    });
    // Note: API doesn't return recipients, so we can't pre-populate them
    setSelectedRecipients([]);

    if (isAdmin) {
      await fetchAllUsers();
    }

    setShowModal(true);
  };

  const handleDeleteContact = async (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/${contactToDelete}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setContacts(contacts.filter((contact) => contact._id !== contactToDelete));
      } else {
        alert("Failed to delete contact");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert("Error deleting contact");
    } finally {
      setShowDeleteConfirm(false);
      setContactToDelete(null);
    }
  };

  const handleSaveContact = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      alert("Name and email are required");
      return;
    }

    if (!currentUser?.username || !currentUser?.email) {
      alert("User information not available. Please refresh the page.");
      return;
    }

    try {
      let response;
      // FIXED: Include username and hostEmployee as per API requirements
      const contactData = {
        ...formData,
        username: currentUser.username, // Backend expects lowercase 'username'
        hostEmployee: currentUser.email,
        recipients: selectedRecipients,
      };

      if (editingContact) {
        // Update existing contact
        response = await fetch(`${API_BASE_URL}/${editingContact._id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactData),
        });

        if (response.ok) {
          const updatedContact = await response.json();
          setContacts(
            contacts.map((contact) =>
              contact._id === editingContact._id
                ? updatedContact.contact || updatedContact
                : contact
            )
          );
        } else {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = errorData.error || "Unknown error";

          // Handle duplicate email error
          if (
            errorMessage.includes("E11000") &&
            errorMessage.includes("email")
          ) {
            errorMessage = `A contact with email "${formData.email}" already exists in the system. Please use a different email or contact an administrator.`;
          }

          alert(`Failed to update contact: ${errorMessage}`);
          return;
        }
      } else {
        // Create new contact
        response = await fetch(API_BASE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactData),
        });

        if (response.ok) {
          const result = await response.json();
          const newContact = result.contact || result;
          newContact.isOwnContact = true;

          if (newContact.shared && selectedRecipients.length > 0) {
            newContact.shareSummary = {
              accepted: 0,
              pending: selectedRecipients.length,
              rejected: 0
            };
          }

          setContacts([...contacts, newContact]);
        } else {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = errorData.error || "Unknown error";

          // Handle duplicate email error
          if (
            errorMessage.includes("E11000") &&
            errorMessage.includes("email")
          ) {
            errorMessage = `A contact with email "${formData.email}" already exists in the system.\n\nThis contact may belong to another user or may be in pending shared contacts.\n\nPlease use a different email or contact an administrator.`;
          }

          alert(`Failed to create contact: ${errorMessage}`);
          return;
        }
      }

      setShowModal(false);
      setFormData({
        name: "",
        email: "",
        company: "",
        shared: false,
      });
      setSelectedRecipients([]);
      setEditingContact(null);
    } catch (error) {
      console.error("Error saving contact:", error);
      alert("Error saving contact: " + error.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: "",
      email: "",
      company: "",
      shared: false,
    });
    setSelectedRecipients([]);
    setEditingContact(null);
  };

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      !searchQuery ||
      contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchQuery.toLowerCase());

    // FIXED: Non-admin users can see their own contacts OR shared contacts
    const canViewContact =
      isAdmin || contact.shared === true || contact.isOwnContact === true;

    return matchesSearch && canViewContact;
  });

  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentContacts = filteredContacts.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        onLogout={handleLogout}
        currentLocation={currentLocation}
        navigate={navigate}
      />
      <ContactModal
        showModal={showModal}
        editingContact={editingContact}
        formData={formData}
        setFormData={setFormData}
        handleCloseModal={handleCloseModal}
        handleSaveContact={handleSaveContact}
        selectedRecipients={selectedRecipients}
        setSelectedRecipients={setSelectedRecipients}
        allUsers={allUsers}
      />

      <ConfirmationModal
        show={showDeleteConfirm}
        message="Are you sure you want to delete this contact?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setContactToDelete(null);
        }}
      />

      {/* Main Content */}
      <div className="ml-32 mr-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-semibold text-gray-900">Address Book</h1>
          <button
            onClick={handleNewContact}
            className="bg-[#ce5841] text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Contact
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
          <table className="w-full overflow-visible">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : currentContacts.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {searchQuery
                      ? "No contacts found matching your search"
                      : "No contacts found"}
                  </td>
                </tr>
              ) : (
                currentContacts.map((contact) => (
                  <tr key={contact._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {contact.name || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {contact.email || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {contact.company || "-"}
                    </td>

                    <td className="px-6 py-4 text-sm">
                      <div className="flex justify-start items-center">
                        <div className="relative inline-block">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer ${
                              contact.isOwnContact
                                ? contact.shared
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                            onMouseEnter={() => setHoveredContact(contact._id)}
                            onMouseLeave={() => setHoveredContact(null)}
                          >
                            {contact.isOwnContact
                              ? contact.shared
                                ? "Shared by Me"
                                : "My Contact"
                              : "Shared with Me"}
                          </span>

                          {/* Tooltip - shows on hover */}
                          {hoveredContact === contact._id &&
                            contact.shared &&
                            contact.isOwnContact && (
                              <ShareDetailsTooltip
                                contact={contact}
                                allUsers={allUsers}
                              />
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="p-2 text-[#ce5841] hover:bg-orange-50 rounded-lg transition-colors"
                          title="Edit contact"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete contact"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Showing {currentContacts.length}
            </span>
            <span className="text-sm text-gray-700">
              of {filteredContacts.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded ${
                      currentPage === pageNum
                        ? "bg-[#ce5841] text-white border-[#ce5841]"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressBook;